<template>
  <v-wait for="dashboard">
    <div class="gs-section">
      <div class="gs-section__toolbar">
        <p class="gs-section__count">Your results for the selected dates</p>

        <div class="gs-stats-filter gs-refresh-controls">
          <date-range-picker
            ref="picker"
            v-model="dashboardDateRange"
            :opens="opens"
            :auto-apply="autoApply"
            :max-date="maxDateForFilter"
            :start-date="customStartDate"
            :end-date="customEndDate"
            :ranges="customDateRanges"
          >
            <div slot="input" slot-scope="picker">
              {{ picker.startDate | moment('MM/DD/YYYY') }} -
              {{ picker.endDate | moment('MM/DD/YYYY') }}
            </div>
          </date-range-picker>

          <gs-button variant="outline" @click="fetchDashboard()">
            {{ $t('frontend_submit') }}
          </gs-button>
        </div>
      </div>

      <div class="gs-stat-grid">
        <div v-for="stat in stats" :key="stat.key" class="gs-stat" :data-stat="stat.key">
          <p class="gs-stat__label">{{ stat.title }}</p>
          <!-- `numFormat` runs through numeral, which parses "$12.50" down to
               13 — the commissions figure arrives from the API already
               formatted, so only plain numbers get the thousands separator. -->
          <p class="gs-stat__value">
            <template v-if="isNumeric(stat.data)">{{ stat.data | numFormat }}</template>
            <template v-else>{{ stat.data }}</template>
          </p>
        </div>
      </div>
    </div>
  </v-wait>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dashboardDateRange: {
        startDate: moment(),
        endDate: moment(),
      },
      opens: 'left',
      autoApply: true,
      maxDateForFilter: new Date(),
      customStartDate: moment(),
      customEndDate: moment(),
      customDateRanges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
        'Last 7 Days': [moment().subtract(6, 'days'), moment()],
        'Last 30 Days': [moment().subtract(29, 'days'), moment()],
        'This Week': [moment().startOf('week'), moment().endOf('week')],
        'Last Week': [
          moment().subtract(1, 'week').startOf('week'),
          moment().subtract(1, 'week').endOf('week'),
        ],
        'This Month': [moment().startOf('month'), moment().endOf('month')],
        'Last Month': [
          moment().subtract(1, 'month').startOf('month'),
          moment().subtract(1, 'month').endOf('month'),
        ],
        'This Year': [moment().startOf('year'), moment().endOf('year')],
        'Last Year': [
          moment().subtract(1, 'year').startOf('year'),
          moment().subtract(1, 'year').endOf('year'),
        ],
        'All Dates': [moment('20191201', 'YYYYMMDD'), moment()],
      },
      impressions: 0,
      uniques: 0,
      sales: 0,
      optins: 0,
      commissions: 0,
    };
  },
  computed: {
    stats() {
      return [
        { key: 'impressions', title: this.$t('frontend_resources_impressions'), data: this.impressions },
        { key: 'uniques', title: this.$t('frontend_resources_uniques'), data: this.uniques },
        { key: 'optins', title: this.$t('frontend_resources_signups'), data: this.optins },
        { key: 'sales', title: this.$t('frontend_resources_sales'), data: this.sales },
        { key: 'commissions', title: this.$t('frontend_resources_commisions'), data: this.commissions },
      ];
    },
  },
  async mounted() {
    await this.fetchDashboard();
  },
  methods: {
    // A finite number, or a string that is nothing but a number, is safe to
    // format; anything else (currency strings, dashes, blanks) is shown as-is.
    isNumeric(value) {
      if (typeof value === 'number') {
        return isFinite(value);
      }

      if (typeof value !== 'string' || value.trim() === '') {
        return false;
      }

      return isFinite(Number(value));
    },
    async fetchDashboard() {
      this.$wait.start('dashboard');
      const dashboardData = await axios.get(
        `affiliates/${this.funnelResource.funnel_id}/dashboard`,
        {
          params: {
            fromDate: moment(this.dashboardDateRange.startDate).format('MM/DD/YYYY'),
            toDate: moment(this.dashboardDateRange.endDate).format('MM/DD/YYYY'),
          },
        }
      );
      this.impressions = dashboardData.data.impressions;
      this.uniques = dashboardData.data.uniques;
      this.sales = dashboardData.data.sales;
      this.optins = dashboardData.data.optins;
      this.commissions = dashboardData.data.commissions;
      this.$wait.end('dashboard');
    },
  },
};
</script>

<style scoped>
.gs-stats-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.gs-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  min-width: 0;
}

.gs-stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 16px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-stat__label {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.gs-stat__value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  overflow-wrap: anywhere;
}

@media (max-width: 640px) {
  .gs-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gs-stat {
    padding: 14px;
  }

  .gs-stat__value {
    font-size: 22px;
  }

  .gs-stats-filter {
    width: 100%;
  }
}
</style>
